import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    Output
} from '@angular/core';
import { ScreenAnalyticsDirective } from '@flyfreely-portal-ui/analytics';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    CombinedMissionStatus,
    GqlMissionCriteria,
    MaintenanceActivityDto,
    MissionService,
    MissionSortFieldName,
    MissionSortFields,
    MissionSummary,
    MissionSummaryDto,
    WorkTracker,
    revertCombinedStatus,
    validatedSortEntryArray
} from '@flyfreely-portal-ui/flyfreely';
import {
    FormatColumnArrayPipe,
    FormatDateTimePipe,
    FormatMissionStatusPipe,
    FormatPersonPipe,
    MissionStatusClassPipe
} from '@flyfreely-portal-ui/ui';
import {
    ColumnSortPreferences,
    TableColumn,
    TableConfig
} from 'libs/flyfreely-table/src/lib/interfaces';
import { findRpic } from 'libs/missions/src/lib/helpers';
import { MissionDialoguesService } from 'libs/missions/src/lib/mission-dialogues.service';
import moment from 'moment';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LinkTestFlightDataService } from './link-flight-test-data.service';

interface LinkableMission extends MissionSummary {
    craftNames?: string[];
    combinedStatus?: CombinedMissionStatus;
    convertedDate: moment.Moment;
}

@Component({
    selector: 'link-test-flight',
    templateUrl: './link-flight-test.component.html',
    providers: [
        LinkTestFlightDataService,
        MissionStatusClassPipe,
        ScreenAnalyticsDirective
    ]
})
export class LinkTestFlightComponent {
    @Input() organisationId: number;
    @Input() maintenanceActivity: MaintenanceActivityDto;
    @Input() rpaId: number;

    @Output() linkedMissionId = new EventEmitter<number>();

    availableColumns: TableColumn[];
    selectedColumns: string[];
    tableConfig: TableConfig;
    tableSorting: ColumnSortPreferences;
    missionStatusFilters: MissionSummaryDto.Status[];
    tableSearch: Record<string, unknown>;

    /** Criteria derived from the current table search state. */
    private searchCriteria: GqlMissionCriteria = {};

    missions$ = new Subject<LinkableMission[]>();

    public working: boolean;
    private workTracker = new WorkTracker();

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private linkTestFlightDataService: LinkTestFlightDataService,
        private missionService: MissionService,
        private commonDialoguesService: CommonDialoguesService,
        private formatDateTimePipe: FormatDateTimePipe,
        private missionStatusPipe: FormatMissionStatusPipe,
        private formatPersonPipe: FormatPersonPipe,
        private formatColumnArrayPipe: FormatColumnArrayPipe,
        private missionDialogueService: MissionDialoguesService,
        private modal: BsModalRef,
        private changeDetector: ChangeDetectorRef
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.linkTestFlightDataService.missions$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(missions => this.updateMissionsList(missions));

        this.linkTestFlightDataService.totalItems$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(totalItems => {
                const currentConfig = this.tableConfig;
                this.tableConfig = {
                    ...currentConfig,
                    totalItems: totalItems
                };
            });

        this.setupTableColumns();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.missions$.complete();
    }

    setupTableColumns() {
        const statusFilters = this.missionService.getCombinedStatusFilters();

        const standardColumns: TableColumn[] = [
            {
                value: 'organisationName',
                name: 'Organisation',
                searchable: false,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'name',
                name: 'Name',
                searchable: true,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'uid',
                name: 'UID',
                searchable: true,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'convertedDate',
                key: 'missionDate',
                name: 'Date/Time',
                searchable: 'daterange',
                selectable: false,
                defaultSelection: true,
                formatterFunction: t => this.formatDateTimePipe.transform(t),
                compareFunction: (a, b) => 0
            },
            {
                value: 'locationName',
                name: 'Location',
                searchable: true,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'combinedStatus',
                name: 'Status',
                searchable: true,
                selectable: false,
                defaultSelection: true,
                sortable: false,
                searchOptions: statusFilters,
                formatterFunction: s => this.missionStatusPipe.transform(s)
            },
            {
                value: 'rpic',
                name: 'RPIC',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                formatterFunction: p => this.formatPersonPipe.transform(p)
            },
            {
                value: 'craftNicknames',
                name: 'RPA',
                searchable: this.rpaId == null,
                selectable: false,
                sortable: false,
                defaultSelection: true,
                formatterFunction: a =>
                    this.formatColumnArrayPipe.transform(a, 1)
            },
            {
                value: 'missionWorkflowVersion.name',
                key: 'missionWorkflowVersion.name',
                name: 'Workflow',
                searchable: true,
                selectable: false,
                defaultSelection: true
            }
        ];

        this.availableColumns = standardColumns;
        this.tableConfig = {
            serverPagination: true,
            currentPage: 0,
            limit: 10,
            actions: [
                {
                    action: (item: MissionSummaryDto) =>
                        this.selectMission(item),
                    icon: 'fal fa-link',
                    tooltip: 'Link mission to maintenance as test flight'
                }
            ]
        };

        this.tableSorting = this.tableSorting ?? null;
        this.selectedColumns = this.selectedColumns ?? null;

        this.tableSearch = this.buildDefaultTableSearch();
        this.onTableSearch(this.tableSearch);
    }

    /**
     * Builds the initial table search state using the maintenance activity's
     * start and end dates as the default date range filter.
     */
    private buildDefaultTableSearch(): Record<string, unknown> {
        const endDate = this.maintenanceActivity.endTime
            ? moment(this.maintenanceActivity.endTime)
                  .endOf('day')
                  .toISOString()
            : null;

        const startDate = this.maintenanceActivity.startDate
            ? moment(this.maintenanceActivity.startDate)
                  .startOf('day')
                  .toISOString()
            : this.maintenanceActivity.endTime != null
            ? moment(this.maintenanceActivity.endTime)
                  .startOf('day')
                  .toISOString()
            : null;

        return startDate != null && endDate != null
            ? { missionDate: [startDate, endDate] }
            : {};
    }

    /**
     * Converts a single table search field and its value to the corresponding
     * GqlMissionCriteria entries.
     */
    private toSearchEntryUpdate(
        field: string,
        value: unknown
    ): Partial<GqlMissionCriteria> {
        if (field === 'missionDate') {
            if (Array.isArray(value) && value.length === 2) {
                return { startTime: value[0], endTime: value[1] };
            }
            return { startTime: null, endTime: null };
        }

        if (field === 'combinedStatus') {
            const deconstructed = revertCombinedStatus(
                value as CombinedMissionStatus | null
            );
            this.missionStatusFilters = deconstructed?.missionStatus ?? null;
            return {
                status: deconstructed?.missionStatus ?? null,
                missionApprovalStatus: deconstructed?.approvalStatus ?? null,
                readyForFinalisation:
                    deconstructed?.readyForFinalisation ?? null,
                outstandingApprovals:
                    deconstructed?.outstandingApprovals ?? null
            };
        }

        const fieldMap: Partial<Record<string, keyof GqlMissionCriteria>> = {
            'missionWorkflowVersion.name': 'workflowName',
            craftNicknames: 'rpaName',
            name: 'name',
            uid: 'uid',
            locationName: 'locationName'
        };
        const criteriaField =
            fieldMap[field] ?? (field as keyof GqlMissionCriteria);
        return { [criteriaField]: value ?? null };
    }

    refreshMissions() {
        const criteria: GqlMissionCriteria = {
            ...this.searchCriteria,
            organisationId: this.organisationId,
            ...(this.rpaId != null ? { rpaId: this.rpaId } : {})
        };

        const sortFields = validatedSortEntryArray(
            this.tableSorting?.column as MissionSortFieldName,
            this.tableSorting?.ascending,
            MissionSortFields
        );

        this.linkTestFlightDataService.findMissions(
            this.tableConfig.currentPage,
            this.tableConfig.limit,
            sortFields,
            criteria
        );
    }

    private updateMissionsList(missions: MissionSummary[]) {
        this.missions$.next(
            missions.map(m => ({
                ...m,
                rpic: findRpic(m.missionCrew),
                combinedStatus: this.missionService.getCombinedStatus(m),
                convertedDate: moment(m.missionDate).tz(m.timeZone)
            }))
        );
    }

    showMission(mission: MissionSummary) {
        this.missionDialogueService.showMissionDetails(mission.id, false);
    }

    updateSelectedColumns(selectedColumns: string[]) {
        this.selectedColumns = selectedColumns;
        if (!this.selectedColumns.includes(this.tableSorting?.column)) {
            this.tableSorting = null;
        }
        this.refreshMissions();
    }

    updateColumnSorting(sorting: ColumnSortPreferences) {
        this.tableSorting = sorting;
        this.tableConfig.currentPage = 0;
        this.refreshMissions();
    }

    onTableSearch(search: Record<string, unknown>) {
        this.tableSearch = search;
        this.missionStatusFilters = null;

        this.searchCriteria =
            search == null || Object.keys(search).length === 0
                ? {}
                : Object.keys(search)
                      .filter(
                          k =>
                              this.availableColumns?.find(
                                  c => c.key === k || c.value === k
                              )?.searchable
                      )
                      .reduce(
                          (acc, k) => ({
                              ...acc,
                              ...this.toSearchEntryUpdate(k, search[k])
                          }),
                          {}
                      );

        this.tableConfig.currentPage = 0;
        // This is needed because the static table uses onPush change detection.
        // Whenever a query repeats a previous output there is a chance the table won't update without explicit change detection.
        this.changeDetector.detectChanges();
        this.refreshMissions();
    }

    onTablePageChanged(page: number) {
        this.tableConfig.currentPage = page;
        this.refreshMissions();
    }

    selectMission(mission: MissionSummaryDto) {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Link mission as test flight',
                `Do you want to link the mission "${mission.name}" as a test flight?`,
                'Yes',
                () =>
                    new Promise<void>(resolve => {
                        this.linkedMissionId.emit(mission.id);
                        resolve();
                    })
            )
            .then(() => this.modal.hide());
    }

    close() {
        this.modal.hide();
    }
}
